import React from 'react';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';

/**
 * Used in:
 *   - All auth pages for displaying API errors
 *   - Any component that needs to show notifications
 *   - Form submissions and API responses
 */
export const Alert = ({ 
  type = 'info', 
  title, 
  message, 
  onClose, 
  className = '',
  show = true 
}) => {
  if (!show) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-success-400" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-error-400" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-warning-400" />;
      default:
        return <Info className="h-5 w-5 text-info-400" />;
    }
  };

  const getStyles = () => {
    switch (type) {
      case 'success':
        return 'bg-success-50 border-success-200 text-success-800';
      case 'error':
        return 'bg-error-50 border-error-200 text-error-800';
      case 'warning':
        return 'bg-warning-50 border-warning-200 text-warning-800';
      default:
        return 'bg-info-50 border-info-200 text-info-800';
    }
  };

  const getCloseButtonStyles = () => {
    switch (type) {
      case 'success':
        return 'text-gray-400 hover:text-gray-600 focus:ring-2 focus:ring-offset-2 focus:ring-offset-success-50 focus:ring-success-500';
      case 'error':
        return 'text-gray-400 hover:text-gray-600 focus:ring-2 focus:ring-offset-2 focus:ring-offset-error-50 focus:ring-error-500';
      case 'warning':
        return 'text-gray-400 hover:text-gray-600 focus:ring-2 focus:ring-offset-2 focus:ring-offset-warning-50 focus:ring-warning-500';
      default:
        return 'text-gray-400 hover:text-gray-600 focus:ring-2 focus:ring-offset-2 focus:ring-offset-info-50 focus:ring-info-500';
    }
  };

  return (
    <div className={`rounded-lg border p-4 ${getStyles()} ${className}`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {getIcon()}
        </div>
        <div className="ml-3 flex-1">
          {title && (
            <h3 className="text-sm font-medium">
              {title}
            </h3>
          )}
          {message && (
            <div className="mt-1 text-sm">
              {message}
            </div>
          )}
        </div>
        {onClose && (
          <div className="ml-auto pl-3">
            <button
              type="button"
              className={`inline-flex rounded-md bg-transparent focus:outline-none ${getCloseButtonStyles()}`}
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Error Notification - Specialized for API errors
 */
export const ErrorAlert = ({ 
  error, 
  onClose, 
  className = '',
  show = true 
}) => {
  if (!show || !error) return null;

  return (
    <Alert
      type="error"
      title="Error"
      message={error}
      onClose={onClose}
      className={className}
      show={show}
    />
  );
};

/**
 * Success Notification - Specialized for success messages
 */
export const SuccessAlert = ({ 
  message, 
  onClose, 
  className = '',
  show = true 
}) => {
  if (!show || !message) return null;

  return (
    <Alert
      type="success"
      title="Success"
      message={message}
      onClose={onClose}
      className={className}
      show={show}
    />
  );
};
